<script setup lang="ts">
    import { reactive, onMounted } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import BillingOrderEditor from './BillingOrderEditor.vue';
    import {
        listOrders,
        createInvoiceFromOrder,
        deleteOrder,
        invoiceAllReadyToSend,
        loadOrder,
        getBookkeepingAccounts,
        newOrder,
        InvoicableOrder,
        EditableOrder,
    } from './Billing.access';

    const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
    const lastOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10);

    const state = reactive<{
        orders: InvoicableOrder[];
        loading: boolean;
        statusFilter: number;
        recipientFilter: string;
        from: string;
        to: string;
        editorVisible: boolean;
        editingOrder: EditableOrder | null;
        bookkeepingAccounts: string[];
    }>({
        orders: [],
        loading: false,
        statusFilter: 1,
        recipientFilter: '',
        from: firstOfMonth,
        to: lastOfMonth,
        editorVisible: false,
        editingOrder: null,
        bookkeepingAccounts: [],
    });

    onMounted(async () => {
        try {
            state.bookkeepingAccounts = await getBookkeepingAccounts();
        } catch {
            // non-critical; editor will show empty dropdown
        }
    });

    function formatDate(dateStr: string | null): string {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString();
    }

    async function fetchOrders(): Promise<void> {
        state.loading = true;
        try {
            state.orders = await listOrders(state.statusFilter, state.from, state.to, state.recipientFilter);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load orders' });
        } finally {
            state.loading = false;
        }
    }

    function openNewOrder(): void {
        state.editingOrder = newOrder();
        state.editorVisible = true;
    }

    async function openEditOrder(order: InvoicableOrder): Promise<void> {
        try {
            state.editingOrder = await loadOrder(order.OrderId);
            state.editorVisible = true;
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load order' });
        }
    }

    function onEditorSaved(): void {
        state.editorVisible = false;
        fetchOrders();
    }

    async function createInvoice(order: InvoicableOrder): Promise<void> {
        try {
            await createInvoiceFromOrder(order.OrderId);
            state.orders = state.orders.filter((o) => o.OrderId !== order.OrderId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Invoice created' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to create invoice' });
        }
    }

    async function removeOrder(order: InvoicableOrder): Promise<void> {
        if (!confirm(`Delete order for ${order.CompanyName}?`)) return;
        try {
            await deleteOrder(order.OrderId);
            state.orders = state.orders.filter((o) => o.OrderId !== order.OrderId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Order deleted' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to delete order' });
        }
    }

    async function invoiceAll(): Promise<void> {
        if (!confirm('Invoice all orders that are ready to send?')) return;
        state.loading = true;
        try {
            const result = await invoiceAllReadyToSend();
            if (result.InvoicesGenerated > 0) {
                bsAlert({ type: AlertTypes.Success, title: 'Invoices generated', content: `${result.InvoicesGenerated} invoices generated` });
            } else {
                bsAlert({ type: AlertTypes.Warning, title: 'No invoices generated', content: 'No orders were ready to send' });
            }
            if (result.Failures.length > 0) {
                bsAlert({ type: AlertTypes.Danger, title: 'Failures', content: result.Failures.join(', ') });
            }
            await fetchOrders();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to invoice all' });
            state.loading = false;
        }
    }
</script>

<template>
    <div class="row m-sm-b">
        <div class="col-xs-12">
            <button type="button" class="btn btn-success" @click="openNewOrder">
                <span class="glyphicon glyphicon-plus"></span>
                Create order
            </button>
        </div>
    </div>

    <div class="row">
        <div class="col-md-2">
            <div class="form-group">
                <label>Status</label>
                <select
                    :value="state.statusFilter"
                    class="form-control"
                    @change="state.statusFilter = Number(($event.target as HTMLSelectElement).value)">
                    <option :value="1">Ready to send</option>
                    <option :value="2">Invoiced - Unpaid</option>
                    <option :value="4">Waiting</option>
                    <option :value="0">All</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Recipient name</label>
                <input
                    type="text"
                    class="form-control"
                    placeholder="Name starts with"
                    :value="state.recipientFilter"
                    @input="state.recipientFilter = ($event.target as HTMLInputElement).value" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>From</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.from"
                    @change="state.from = ($event.target as HTMLInputElement).value" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>To</label>
                <input
                    type="date"
                    class="form-control"
                    :value="state.to"
                    @change="state.to = ($event.target as HTMLInputElement).value" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>&nbsp;</label>
                <div>
                    <button type="button" class="btn btn-primary" :disabled="state.loading" @click="fetchOrders">List</button>
                </div>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>&nbsp;</label>
                <div>
                    <button type="button" class="btn btn-warning" :disabled="state.loading" @click="invoiceAll">Invoice all ready</button>
                </div>
            </div>
        </div>
    </div>

    <div v-if="state.loading">Loading...</div>

    <div v-if="state.orders.length > 0" class="table-responsive">
        <table class="table table-hover table-condensed">
            <thead class="small">
                <tr>
                    <th>Company</th>
                    <th class="text-right">Amount</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created date</th>
                    <th>Created by</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="order in state.orders" :key="order.OrderId">
                    <td>{{ order.CompanyName }}</td>
                    <td class="text-right">{{ order.Amount }} {{ order.CurrencyCode }}</td>
                    <td>{{ order.OrderType }}</td>
                    <td>{{ order.StatusString }}</td>
                    <td>{{ formatDate(order.OrderDate) }}</td>
                    <td>{{ order.User?.UserName }}</td>
                    <td>
                        <div class="btn-group">
                            <button type="button" class="btn btn-sm btn-default dropdown-toggle" data-toggle="dropdown">
                                <span class="glyphicon glyphicon-option-vertical"></span>
                            </button>
                            <ul class="dropdown-menu dropdown-menu-right">
                                <li>
                                    <a href="#" @click.prevent="openEditOrder(order)">Edit order</a>
                                </li>
                                <li v-if="order.StatusString === 'ReadyToSend'">
                                    <a href="#" @click.prevent="createInvoice(order)">Create invoice</a>
                                </li>
                                <li v-if="order.StatusString === 'ReadyToSend'">
                                    <a href="#" @click.prevent="removeOrder(order)">Delete order</a>
                                </li>
                            </ul>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <BillingOrderEditor
        :show="state.editorVisible"
        :order="state.editingOrder"
        :bookkeeping-accounts="state.bookkeepingAccounts"
        @close="state.editorVisible = false"
        @saved="onEditorSaved" />
</template>

<script lang="ts">
    export default { name: 'BillingOrders' };
</script>

<style></style>
